import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Smartphone, LogOut, Plus, Trophy, Shield, Globe, ArrowRight, RefreshCw, Unlink } from "lucide-react";
// iter87.180.15 — BrutalConfirm for the async unlink path (iOS
// Safari drops native confirms inside async handlers).
import { useBrutalConfirm } from "@/components/BrutalConfirm";

/**
 * iter86.75 — /tester: Mobile Stand-In Program shell.
 * Simulates the future mobile app: one Mobile ID (username+password),
 * claim identities across rivalries, aggregated home, enter-league proxy.
 * Scope: /app/memory/TESTER_MOBILE_STANDIN.md
 */
const SESSION_KEY = "tcr_mobile_session_v1";
// iter86.77.4 — Environment-aware switcher (user request): on preview the
// dropdown offers "Preview" + "Beta"; on beta (*.tinycrewrivalries.com) it
// offers ONLY "Beta" — no path back into preview from the beta tester.
// External production will be appended here when it exists.
const IS_BETA = /tinycrewrivalries\.com$/i.test(window.location.hostname);
const CURRENT_ENV = IS_BETA ? "beta" : "preview";
const ENVIRONMENTS = IS_BETA
    ? [{ key: "beta", label: "Beta", host: "https://beta.tinycrewrivalries.com" }]
    : [
        { key: "preview", label: "Preview", host: "" },
        { key: "beta", label: "Beta", host: "https://beta.tinycrewrivalries.com" },
    ];

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

function getSession() {
    try { return JSON.parse(localStorage.getItem(SESSION_KEY)); } catch { return null; }
}

export default function TesterApp() {
    const navigate = useNavigate();
    const [programEnabled, setProgramEnabled] = useState(null);
    const [session, setSession] = useState(getSession);
    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({ username: "", password: "", invite_code: "" });
    const [leagues, setLeagues] = useState([]);
    const [claimOpen, setClaimOpen] = useState(false);
    const [claim, setClaim] = useState({ league_code: "", personal_code: "", username: "" });
    const [busy, setBusy] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [tenantName, setTenantName] = useState(null);
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();

    useEffect(() => {
        api.get("/mobile/tenant-by-host", { params: { host: window.location.host } })
            .then((r) => setTenantName(r.data?.name || null))
            .catch(() => {});
    }, []);

    const authHdrs = useCallback(() => ({ Authorization: `Bearer ${session?.token}` }), [session]);

    useEffect(() => {
        api.get("/mobile/program-status")
            .then((r) => setProgramEnabled(!!r.data.enabled))
            .catch(() => setProgramEnabled(false));
    }, []);

    const loadLeagues = useCallback(async () => {
        if (!session?.token) return;
        try {
            const r = await api.get("/mobile/my-leagues", { headers: { Authorization: `Bearer ${session.token}` } });
            setLeagues(r.data.leagues || []);
        } catch (e) {
            if (e?.response?.status === 401 || e?.response?.status === 403) {
                localStorage.removeItem(SESSION_KEY);
                setSession(null);
                toast.error(e?.response?.data?.detail || "Session expired — log in again");
            }
        }
    }, [session]);
    useEffect(() => { loadLeagues(); }, [loadLeagues]);

    const refresh = async () => {
        setRefreshing(true);
        await loadLeagues();
        setRefreshing(false);
        toast.success("Standings & to-dos refreshed");
    };

    const unlink = async (lg) => {
        const ok = await brutalConfirm({
            title: `Unlink "${lg.username}"?`,
            body: `Unlink this identity from ${lg.league_name}. Another Mobile ID will then be able to claim it.`,
            confirmLabel: "Unlink",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/mobile/memberships/${lg.membership_id}`, { headers: authHdrs() });
            toast.success("Identity unlinked");
            loadLeagues();
        } catch (e) { toast.error(e?.response?.data?.detail || "Unlink failed"); }
    };

    const submitAuth = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const path = mode === "login" ? "/mobile/auth/login" : "/mobile/auth/register";
            const body = mode === "login"
                ? { username: form.username, password: form.password }
                : { username: form.username, password: form.password, invite_code: form.invite_code };
            const r = await api.post(path, body);
            const s = { token: r.data.token, user: r.data.user };
            localStorage.setItem(SESSION_KEY, JSON.stringify(s));
            setSession(s);
            toast.success(mode === "login" ? `Welcome back, ${s.user.username}` : "Mobile ID created");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const submitClaim = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/mobile/claim-league", claim, { headers: authHdrs() });
            toast.success("Rivalry claimed — added to your account");
            setClaimOpen(false);
            setClaim({ league_code: "", personal_code: "", username: "" });
            loadLeagues();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Claim failed — check the codes");
        }
        setBusy(false);
    };

    const enterLeague = (lg) => {
        // Enter-league proxy: swap into the claimed identity and load the
        // existing game screens — the tester never re-enters codes.
        localStorage.setItem("pickem_session_v1", JSON.stringify({
            league_id: lg.league_id, league_name: lg.league_name,
            invite_code: lg.invite_code, participant_id: lg.participant_id,
            username: lg.username, email: null, is_admin: lg.role === "admin",
        }));
        navigate(`/league/${lg.league_id}`);
    };

    const logout = () => { localStorage.removeItem(SESSION_KEY); setSession(null); setLeagues([]); };

    const switchEnv = (key) => {
        const env = ENVIRONMENTS.find((x) => x.key === key);
        if (!env || key === CURRENT_ENV || !env.host) return;
        window.location.href = `${env.host}/tester`;
    };

    if (programEnabled === null) return <div className="min-h-screen bg-ink" />;
    if (!programEnabled) {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center p-6" data-testid="tester-disabled-screen">
                <div className="brutal-card p-8 max-w-md text-center space-y-3">
                    <Smartphone className="w-8 h-8 text-zinc-600 mx-auto" />
                    <h1 className="font-display font-extrabold text-lg uppercase tracking-wider text-white">Tester Program Offline</h1>
                    <p className="text-sm text-zinc-400">The mobile tester program is not enabled in this environment.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ink text-white">
            <header className="border-b-2 border-[#222] px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-cyber" />
                    <span className="font-display font-black uppercase tracking-widest text-sm">TCR · Mobile Stand-In</span>
                    <span className="text-[9px] uppercase tracking-widest text-black bg-volt px-1.5 py-0.5 rounded font-bold">Tester</span>
                    {tenantName && (
                        <span data-testid="tester-tenant-badge" className="text-[9px] uppercase tracking-widest text-cyber border border-cyber/40 px-1.5 py-0.5 rounded font-bold">
                            {tenantName}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5 text-zinc-500" />
                    <select
                        data-testid="tester-env-switcher"
                        className="brutal-input text-xs py-1"
                        value={CURRENT_ENV}
                        onChange={(e) => switchEnv(e.target.value)}
                    >
                        {ENVIRONMENTS.map((env) => (
                            <option key={env.key} value={env.key}>{env.label}</option>
                        ))}
                    </select>
                    {session && (
                        <button data-testid="tester-logout-btn" onClick={logout} className="brutal-btn-ghost text-xs py-1.5 px-2 text-zinc-400 hover:text-heat">
                            <LogOut className="w-3.5 h-3.5" /> {session.user.username}
                        </button>
                    )}
                </div>
            </header>

            <main className="max-w-2xl mx-auto p-4 sm:p-8 space-y-6">
                {!session ? (
                    <div className="brutal-card p-6 space-y-4" data-testid="tester-auth-card">
                        <h1 className="font-display font-extrabold text-xl uppercase tracking-wider">
                            {mode === "login" ? "Mobile ID Login" : "Create Mobile ID"}
                        </h1>
                        <p className="text-xs text-zinc-400 leading-relaxed">
                            One account, all your rivalries — this site stands in for the future mobile app.
                            {mode === "register" && " You need a tester invite code — a Commons admin mints one in Commons Console → Mezzanine → Mobile Testers. Pick a NEW username + password for your Mobile ID (it's a separate account, not your admin login)."}
                        </p>
                        <form onSubmit={submitAuth} className="space-y-3">
                            {mode === "register" && (
                                <input data-testid="tester-invite-input" className="brutal-input w-full" placeholder="Invite code" required
                                    value={form.invite_code} onChange={(e) => setForm({ ...form, invite_code: e.target.value })} />
                            )}
                            <input data-testid="tester-username-input" className="brutal-input w-full" placeholder="Username" required minLength={3}
                                value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                            <input data-testid="tester-password-input" className="brutal-input w-full" type="password" placeholder="Password (min 8 chars)" required minLength={8}
                                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                            <button data-testid="tester-auth-submit-btn" disabled={busy} className="brutal-btn w-full bg-cyber text-black font-bold py-2.5">
                                {mode === "login" ? "Log in" : "Register"}
                            </button>
                        </form>
                        <button data-testid="tester-auth-toggle-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
                            className="text-xs text-zinc-400 hover:text-cyber underline">
                            {mode === "login" ? "Have an invite code? Create a Mobile ID" : "Already registered? Log in"}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between gap-3">
                            <h1 className="font-display font-extrabold text-xl uppercase tracking-wider">My Rivalries</h1>
                            <div className="flex items-center gap-2">
                                <button data-testid="tester-refresh-btn" onClick={refresh} disabled={refreshing}
                                    title="Refresh standings & to-dos"
                                    className="brutal-btn-ghost p-2 text-zinc-400 hover:text-cyber">
                                    <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                                </button>
                                <button data-testid="tester-add-league-btn" onClick={() => setClaimOpen(true)}
                                    className="brutal-btn text-xs py-2 bg-volt text-black font-bold">
                                    <Plus className="w-3.5 h-3.5" /> Claim a rivalry
                                </button>
                            </div>
                        </div>
                        {leagues.length === 0 && (
                            <div className="brutal-card p-6 text-center text-sm text-zinc-400" data-testid="tester-empty-state">
                                No rivalries claimed yet — hit <strong className="text-volt">Claim a rivalry</strong> and enter your league code, personal code and username.
                            </div>
                        )}
                        {claimOpen && (
                            <div className="brutal-card p-5 space-y-3 border-volt" data-testid="tester-claim-card">
                                <h2 className="font-display font-extrabold text-sm uppercase tracking-wider">Claim a rivalry</h2>
                                <p className="text-xs text-zinc-400">Same codes you use on the main site — this links that identity to your Mobile ID.</p>
                                <form onSubmit={submitClaim} className="space-y-2">
                                    <input data-testid="tester-claim-league-code" className="brutal-input w-full" placeholder="League invite code" required
                                        value={claim.league_code} onChange={(e) => setClaim({ ...claim, league_code: e.target.value })} />
                                    <input data-testid="tester-claim-username" className="brutal-input w-full" placeholder="Your username in that rivalry" required
                                        value={claim.username} onChange={(e) => setClaim({ ...claim, username: e.target.value })} />
                                    <input data-testid="tester-claim-personal-code" className="brutal-input w-full" placeholder="Your personal code" required
                                        value={claim.personal_code} onChange={(e) => setClaim({ ...claim, personal_code: e.target.value })} />
                                    <div className="flex gap-2">
                                        <button data-testid="tester-claim-submit-btn" disabled={busy} className="brutal-btn flex-1 bg-volt text-black font-bold py-2">Claim</button>
                                        <button type="button" onClick={() => setClaimOpen(false)} className="brutal-btn-ghost px-3 text-zinc-400">Cancel</button>
                                    </div>
                                </form>
                            </div>
                        )}
                        <div className="space-y-3">
                            {leagues.map((lg) => (
                                <div key={lg.league_id + lg.participant_id} data-testid={`tester-league-${lg.league_id}-${lg.participant_id}`}
                                    className="brutal-card w-full p-4 hover:border-cyber transition-colors">
                                    <div className="flex items-start gap-2">
                                        <button onClick={() => enterLeague(lg)} data-testid={`tester-league-enter-${lg.league_id}-${lg.participant_id}`}
                                            className="flex-1 min-w-0 text-left">
                                    <span className="flex items-center justify-between gap-3">
                                        <span className="flex items-center gap-3 min-w-0">
                                            {lg.role === "admin" ? <Shield className="w-5 h-5 text-volt shrink-0" /> : <Trophy className="w-5 h-5 text-cyber shrink-0" />}
                                            <span className="min-w-0 block">
                                                <span className="font-bold truncate block">{lg.league_name}</span>
                                                <span className="text-[10px] uppercase tracking-widest text-zinc-500 block">
                                                    {lg.role === "admin" ? "League Admin" : "Player"} · as {lg.username}
                                                </span>
                                            </span>
                                        </span>
                                        <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
                                    </span>
                                    {(lg.tournaments || []).length > 0 && (
                                        <span className="mt-3 pt-3 border-t border-zinc-800 space-y-2 block">
                                            {lg.tournaments.map((t) => (
                                                <span key={t.tournament_id} className="flex flex-wrap items-center gap-2"
                                                    data-testid={`tester-tournament-${lg.participant_id}-${t.tournament_id}`}>
                                                    <span className="text-xs font-bold truncate max-w-[11rem]">{t.name}</span>
                                                    <span className={`pill text-[9px] font-bold uppercase border ${
                                                        t.status_label === "Live" ? "border-neon text-neon"
                                                        : t.status_label === "Final" ? "border-cyber text-cyber"
                                                        : t.status_label === "Setup" ? "border-zinc-600 text-zinc-500"
                                                        : "border-cyber text-cyber"}`}>
                                                        {t.status_label}
                                                    </span>
                                                    {t.rank != null && (
                                                        <span className="text-[10px] font-mono text-zinc-400">#{t.rank}{t.points_label ? ` · ${t.points_label}` : ""}</span>
                                                    )}
                                                    {(t.todos || []).map((td) => (
                                                        <span key={td.key} className="pill text-[9px] font-bold uppercase border border-blaze text-blaze bg-blaze/10">
                                                            {td.label}
                                                        </span>
                                                    ))}
                                                    {(t.todos || []).length === 0 && (
                                                        <span className="text-[9px] uppercase tracking-widest text-zinc-600">All caught up ✓</span>
                                                    )}
                                                </span>
                                            ))}
                                        </span>
                                            )}
                                        </button>
                                        <button onClick={() => unlink(lg)} data-testid={`tester-league-unlink-${lg.league_id}-${lg.participant_id}`}
                                            title="Unlink this identity from my Mobile ID"
                                            className="brutal-btn-ghost p-1.5 text-zinc-600 hover:text-heat shrink-0">
                                            <Unlink className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </main>
            {confirmHost}
        </div>
    );
}
